// CoverGenerationModal.tsx
// NOTE: Update frontend/src/__tests__/CoverGenerationModal.test.tsx and run
// `../scripts/test-frontend.sh CoverGenerationModal` when making changes here.
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Input,
  Checkbox,
  message,
  Space,
  Tooltip,
  Popover,
  Spin,
  Select,
} from 'antd';
import {
  BulbOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { getAllLanguages } from './LanguageConfig';

import ModalCostBreakdown, { type ModalCostBreakdownRow } from './components/ModalCostBreakdown';
import FeatherSpinner from './components/FeatherSpinner';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import { imageDataUrlFromBase64 } from './utils/imageMime';
import { resolveFrontendApiBase } from './utils/apiBase';
import { getAuthHeaders } from './utils/authHeaders';
import { coverModelOptions } from './utils/coverModelOptions';
import './CoverModalRefresh.css';

interface CoverGenerationModalProps {
  visible: boolean;
  onClose: () => void;
  bookTitle: string;
  translatedTitle: string;
  seriesName?: string;
  translatedSeriesName?: string;
  targetLanguage: string;
  bookLanguageName?: string;
  currentCoverUrl?: string;
  bookId: string;
  userId: string;
  creditBalance?: any;
  getCoverCreditCost?: (args: { mode: 'modify' | 'generate'; imageCount: number }) => number;
  onGenerationStart: (language: string) => void;
  onGenerationComplete: (images: string[], options?: {
    mode: 'modify' | 'generate';
    replaceTitle?: boolean;
    replaceSeries?: boolean;
    addLanguageSticker?: boolean;
    authorMode?: 'include' | 'omit';
    additionalReplacements?: Array<{ source: string; replacement: string }>;
    originalTitle?: string;
    translatedTitle?: string;
    originalSeries?: string;
    translatedSeries?: string;
    language?: string;
    // New: propagate partial generation info to selection modal
    requestedCount?: number;
    generatedCount?: number;
    failureMessages?: string[];
    partialFailure?: boolean;
  }) => void;
  isTranslationContext?: boolean;

  // Optional metadata for richer default prompt (generate mode)
  authorName?: string;
  genre?: string;
  tropes?: string[] | string;
  keywords?: string[] | string;
  bookNumber?: string | number;

  // New optional props for language selection/metadata
  availableLanguages?: string[];
  getMetadataForLanguage?: (language: string) => { title?: string; series?: string } | undefined;
}

interface GeneratedImage {
  url?: string;
  b64_json?: string;
  revised_prompt?: string;
}

type CoverModelSelection = string;

interface ImageModelOption {
  id: string;
  displayName: string;
  apiModel: string;
  provider: 'openai' | 'gemini' | 'openrouter_image' | string;
  enabledModes?: string[];
  isActive?: boolean;
}

interface GenerateCoverSuccess {
  status: 'success';
  result: GeneratedImage | GeneratedImage[];
  // Optional metadata from backend for partial failures/charging info
  partial_failure?: boolean;
  requested?: number;
  generated?: number;
  charged_credits?: number;
  unit_cost?: number;
  // Optional diagnostic fields if provider returned no images
  note?: string;
  content?: string;
  provider?: string;
  model?: string;
}

interface GenerateCoverError {
  status: 'error';
  message: string;
}

interface GenerateCoverModerationError {
  status: 'moderation_error';
  message: string;
  suggestion: string;
}

type GenerateCoverResponse =
  | GenerateCoverSuccess
  | GenerateCoverError
  | GenerateCoverModerationError;

const API_BASE = resolveFrontendApiBase();
const MAX_COVER_TRANSLATION_GUIDANCE_CHARS = 300;
const DEFAULT_COVER_MODEL = 'gpt-image-2.5-sunburst';
const DEFAULT_COVER_IMAGE_MODELS: ImageModelOption[] = [
  {
    id: DEFAULT_COVER_MODEL,
    displayName: 'OpenAI Image 2.5 Sunburst',
    apiModel: DEFAULT_COVER_MODEL,
    provider: 'openai',
    enabledModes: ['generate', 'modify', 'variation'],
  },
  {
    id: 'gpt-image-2',
    displayName: 'OpenAI Image 2',
    apiModel: 'gpt-image-2',
    provider: 'openai',
    enabledModes: ['generate', 'modify', 'variation'],
  },
  {
    id: 'google/gemini-3.1-flash-image-preview',
    displayName: 'Gemini 3.1 Flash Image (Preview)',
    apiModel: 'google/gemini-3.1-flash-image-preview',
    provider: 'gemini',
    enabledModes: ['generate', 'modify', 'variation'],
  },
];

const CoverGenerationModal: React.FC<CoverGenerationModalProps> = ({
  visible,
  onClose,
  bookTitle,
  translatedTitle,
  seriesName = '',
  translatedSeriesName = '',
  targetLanguage,
  bookLanguageName,
  currentCoverUrl,
  bookId,
  userId,
  creditBalance,
  getCoverCreditCost,
  onGenerationStart,
  onGenerationComplete,
  isTranslationContext = true,
  // optional metadata
  authorName,
  genre,
  tropes,
  keywords,
  bookNumber,
  availableLanguages,
  getMetadataForLanguage,
}) => {
  const hasOriginalCover = !!currentCoverUrl;
  const targetLanguageIsOriginal = targetLanguage === 'Original';
  const shouldOpenInModifyMode = visible && isTranslationContext && !targetLanguageIsOriginal && hasOriginalCover;
  const [mode, setMode] = useState<'modify' | 'generate'>(
    isTranslationContext ? (hasOriginalCover ? 'modify' : 'generate') : 'generate'
  );
  const [provider, setProvider] = useState<CoverModelSelection>(DEFAULT_COVER_MODEL);
  const [generateModel, setGenerateModel] = useState<CoverModelSelection>(DEFAULT_COVER_MODEL);
  const [imageModelOptions, setImageModelOptions] = useState<ImageModelOption[]>(DEFAULT_COVER_IMAGE_MODELS);
  const [imageModelOptionsLoading, setImageModelOptionsLoading] = useState<boolean>(false);
  const [imageCount, setImageCount] = useState<number>(1);
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [coverTranslationGuidance, setCoverTranslationGuidance] = useState<string>('');
  const [rightsConfirmed, setRightsConfirmed] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(targetLanguage);
  const displayLanguage = selectedLanguage === 'Original' ? (bookLanguageName || 'Original') : selectedLanguage;
  const languageOptions = Array.from(new Set(['Original', ...((availableLanguages && availableLanguages.length ? availableLanguages : getAllLanguages(false)) || [])]));

  // Sync selected language to the incoming prop whenever the modal opens or the prop changes
  useEffect(() => {
    if (visible) {
      setSelectedLanguage(targetLanguage);
      if (shouldOpenInModifyMode) {
        setMode('modify');
        setAiAssistLoading(false);
        setPromptWaiting(false);
      } else {
        setMode('generate');
      }
    }
  }, [visible, targetLanguage, shouldOpenInModifyMode]);

  // Text replacement options (only for modify mode)
  const [replaceTitle, setReplaceTitle] = useState<boolean>(true);
  const [replaceSeries, setReplaceSeries] = useState<boolean>(false);
  const [addLanguageSticker, setAddLanguageSticker] = useState<boolean>(false);
  const [includeAuthor, setIncludeAuthor] = useState<boolean>(Boolean(authorName?.trim()));
  const [additionalReplacements, setAdditionalReplacements] = useState<Array<{ source: string; replacement: string }>>([]);
  const additionalReplacementsError = additionalReplacements.some(row => !row.source.trim() || !row.replacement.trim() || row.source.length > 200 || row.replacement.length > 200 || row.source.trim() === row.replacement.trim())
    ? 'Provide different original and replacement text (up to 200 characters each) for every additional row, or remove empty rows.' : '';
  useEffect(() => {
    if (!visible) return;
    try {
      const language = ((selectedLanguage === 'Original' ? 'original' : selectedLanguage) || 'original').toLowerCase();
      const context = JSON.parse(sessionStorage.getItem(`poeai:coverContext:${bookId}:${language}`) || '{}');
      const rows = context?.generationOptions?.additionalReplacements;
      setAdditionalReplacements(Array.isArray(rows) ? rows.slice(0, 10).filter(row => typeof row?.source === 'string' && typeof row?.replacement === 'string') : []);
    } catch { setAdditionalReplacements([]); }
  }, [visible, bookId, selectedLanguage]);
  const [originalTitleText, setOriginalTitleText] = useState<string>(bookTitle);
  const [translatedTitleText, setTranslatedTitleText] = useState<string>(translatedTitle);
  const [originalSeriesText, setOriginalSeriesText] = useState<string>(seriesName);
  const [translatedSeriesText, setTranslatedSeriesText] = useState<string>(translatedSeriesName);
  const hasSeriesName = !!seriesName?.trim();
  const canonicalAuthorName = (authorName || '').trim();
  const replacementFieldsEditedRef = useRef({
    originalTitle: false,
    translatedTitle: false,
    originalSeries: false,
    translatedSeries: false,
  });
  const lastReplacementHydrationKeyRef = useRef<string>('');
  const lastReplacementHydrationLanguageRef = useRef<string>('');

  // AI Assist + deterministic prompt state
  const [deterministicPrompt, setDeterministicPrompt] = useState<string>('');
  const [aiAssistLoading, setAiAssistLoading] = useState<boolean>(false);
  const [promptWaiting, setPromptWaiting] = useState<boolean>(true);
  const [aiAssistBreakdown, setAiAssistBreakdown] = useState<any | null>(null);
  const [isCostBreakdownModalVisible, setIsCostBreakdownModalVisible] = useState(false);
  // Tracks if we've auto-triggered AI Assist for this modal open
  const aiAssistTriggeredRef = useRef<boolean>(false);
  // Suppress AI Assist when switching from Original -> translated language with cover until user returns to Generate
  const suppressAiAssistDueToLangSwitchRef = useRef<boolean>(false);
  const prevSelectedLangRef = useRef<string>(selectedLanguage);

  // Credit cost calculation
  const BASE_COST_GENERATE = 1000;
  const BASE_COST_MODIFY = 500;
  const coverCreditCost =
    getCoverCreditCost
      ? getCoverCreditCost({ mode, imageCount })
      : ((mode === 'generate' ? BASE_COST_GENERATE : BASE_COST_MODIFY) * imageCount);
  const creditBalanceValue = creditBalance?.available_balance ?? creditBalance?.credit_balance;
  const isCreditInfoLoading = creditBalanceValue == null;
  const hasEnoughCredits = !isCreditInfoLoading && creditBalanceValue >= coverCreditCost;
  const normalizedCoverTranslationGuidance = String(coverTranslationGuidance || '').slice(0, MAX_COVER_TRANSLATION_GUIDANCE_CHARS);
  const formatCredits = React.useCallback((value: number) => {
    return new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value)));
  }, []);

  // Update replacement fields from metadata without wiping in-progress user edits.
  useEffect(() => {
    if (!visible) {
      replacementFieldsEditedRef.current = {
        originalTitle: false,
        translatedTitle: false,
        originalSeries: false,
        translatedSeries: false,
      };
      lastReplacementHydrationKeyRef.current = '';
      lastReplacementHydrationLanguageRef.current = '';
      return;
    }
    setIncludeAuthor(Boolean(canonicalAuthorName));

    const isOrig = selectedLanguage === 'Original';
    let tTitle = '';
    let tSeries = '';
    if (!isOrig) {
      if (typeof getMetadataForLanguage === 'function') {
        const info = getMetadataForLanguage(selectedLanguage) || {};
        tTitle = (info.title || '').trim();
        tSeries = (info.series || '').trim();
      } else {
        tTitle = (translatedTitle || '').trim();
        tSeries = (translatedSeriesName || '').trim();
      }
    }

    const nextFields = {
      originalTitle: bookTitle || '',
      translatedTitle: tTitle,
      originalSeries: seriesName || '',
      translatedSeries: tSeries,
    };
    const hydrationKey = JSON.stringify([
      selectedLanguage,
      nextFields.originalTitle,
      nextFields.translatedTitle,
      nextFields.originalSeries,
      nextFields.translatedSeries,
    ]);

    if (lastReplacementHydrationKeyRef.current === hydrationKey) {
      return;
    }

    const languageChanged = lastReplacementHydrationLanguageRef.current !== selectedLanguage;
    if (languageChanged) {
      replacementFieldsEditedRef.current = {
        originalTitle: false,
        translatedTitle: false,
        originalSeries: false,
        translatedSeries: false,
      };
    }
    const edited = replacementFieldsEditedRef.current;

    if (languageChanged || !edited.originalTitle) {
      setOriginalTitleText(nextFields.originalTitle);
    }
    if (languageChanged || !edited.translatedTitle) {
      setTranslatedTitleText(nextFields.translatedTitle);
    }
    if (languageChanged || !edited.originalSeries) {
      setOriginalSeriesText(nextFields.originalSeries);
    }
    if (languageChanged || !edited.translatedSeries) {
      setTranslatedSeriesText(nextFields.translatedSeries);
    }

    lastReplacementHydrationKeyRef.current = hydrationKey;
    lastReplacementHydrationLanguageRef.current = selectedLanguage;
    if (!seriesName?.trim()) {
      setReplaceSeries(false);
    }
  }, [visible, selectedLanguage, getMetadataForLanguage, bookTitle, seriesName, translatedTitle, translatedSeriesName, canonicalAuthorName]);

  // If no original cover is available, ensure we are in generate mode
  useEffect(() => {
    if (!currentCoverUrl && mode === 'modify') {
      setMode('generate');
    }
  }, [currentCoverUrl, visible, mode]);

  // On open, default mode by language: if not Original and an original cover exists => modify; otherwise generate.
  useEffect(() => {
    if (!visible) return;
    const canModify = !!currentCoverUrl;
    if (selectedLanguage !== 'Original' && canModify) {
      setMode('modify');
    } else {
      setMode('generate');
    }
  }, [visible, selectedLanguage, currentCoverUrl]);


  // If Original language is selected while in modify mode, switch to generate
  useEffect(() => {
    if (selectedLanguage === 'Original' && mode === 'modify') {
      setMode('generate');
    }
  }, [selectedLanguage, mode]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setImageModelOptionsLoading(true);
    fetch(`${API_BASE}/api/image-models?surface=covers`)
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('Failed to load image models'))))
      .then((data: ImageModelOption[]) => {
        if (cancelled) return;
        setImageModelOptions(Array.isArray(data) ? data : DEFAULT_COVER_IMAGE_MODELS);
      })
      .catch(() => {
        if (!cancelled) setImageModelOptions(DEFAULT_COVER_IMAGE_MODELS);
      })
      .finally(() => {
        if (!cancelled) setImageModelOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // Respect catalog exclusions and per-mode availability, including admin overrides.
  useEffect(() => {
    const available = (selection: string, targetMode: 'generate' | 'modify') =>
      imageModelOptions.some(model => model.id === selection &&
        (!model.enabledModes?.length || model.enabledModes.includes(targetMode)));
    const fallback = (targetMode: 'generate' | 'modify') =>
      imageModelOptions.find(model => model.id === DEFAULT_COVER_MODEL && available(model.id, targetMode))?.id ||
      imageModelOptions.find(model => available(model.id, targetMode))?.id;
    setGenerateModel(current => available(current, 'generate') ? current : (fallback('generate') || current));
    setProvider(current => available(current, 'modify') ? current : (fallback('modify') || current));
  }, [imageModelOptions]);

  const resolveModelSelection = React.useCallback(
    (
      selection: CoverModelSelection
    ): { provider: 'openai' | 'gemini' | 'openrouter_image'; model: string } => {
      if (selection === 'openai') {
        return { provider: 'openai', model: DEFAULT_COVER_MODEL };
      }
      if (selection === 'gemini') {
        return { provider: 'gemini', model: 'google/gemini-3.1-flash-image-preview' };
      }
      const match = imageModelOptions.find(model => model.id === selection || model.apiModel === selection);
      if (!match) {
        return { provider: 'openai', model: DEFAULT_COVER_MODEL };
      }
      const resolvedProvider = match.provider === 'openrouter_image'
        ? 'openrouter_image'
        : (match.provider === 'gemini' ? 'gemini' : 'openai');
      return { provider: resolvedProvider, model: match.id || match.apiModel };
    },
    [imageModelOptions]
  );

  // Build richer default prompt for generate mode using available metadata
  const asList = (v?: string | string[]) =>
    Array.isArray(v) ? v.map(s => s?.trim()).filter(Boolean).join(', ') : ((v || '').trim());

  const toArray = (v?: string | string[]) =>
    Array.isArray(v)
      ? v.map(s => String(s ?? '').trim()).filter(Boolean)
      : String(v ?? '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);

  // Display a natural language name for a language code or pass-through a full name
  const displayLanguageFull = (v?: string | null): string => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    // Heuristic: language code like en, pt-BR, zh-CN
    if (/^[A-Za-z]{2,3}([-_][A-Za-z]{2})?$/.test(s)) {
      try {
        // Use English display names for consistency
        const dn: any = (Intl as any).DisplayNames ? new (Intl as any).DisplayNames(['en'], { type: 'language' }) : null;
        const name = dn?.of?.(s.toLowerCase());
        if (name && typeof name === 'string') {
          return name.charAt(0).toUpperCase() + name.slice(1);
        }
      } catch {}
      // Minimal fallback mapping for common cases
      const map: Record<string, string> = {
        en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian', pt: 'Portuguese', 'pt-br': 'Brazilian Portuguese', 'pt-pt': 'European Portuguese', ar: 'Arabic', he: 'Hebrew', fa: 'Persian', ur: 'Urdu', hi: 'Hindi', ja: 'Japanese', ko: 'Korean', zh: 'Chinese', 'zh-cn': 'Simplified Chinese', 'zh-tw': 'Traditional Chinese'
      };
      const k = s.toLowerCase();
      if (map[k]) return map[k];
    }
    return s;
  };

  // Build a generation-ready prompt from AI Assist structured summary
  const buildPromptFromSummary = (summary: any): string => {
    try {
      const bf = (summary?.text_fields || {}) as any;
      const dz = (summary?.design || {}) as any;
      const imagery: string[] = Array.isArray(summary?.imagery) ? summary.imagery : [];
      const constraints: string[] = Array.isArray(summary?.constraints) ? summary.constraints : [];

      const t = (bf.title || chosenTitle || '').trim();
      const a = (bf.author || authorName || '').trim();
      const s = (bf.series || chosenSeries || '').trim();
      const sn = (bf.series_number || String(bookNumber ?? '').trim()).trim();
      const lang = displayLanguageFull(dz.language || selectedLanguage);
      const dir = (dz.direction || '').toString().toUpperCase() === 'RTL' ? 'RTL' : 'LTR';
      const aspectRaw = (dz.aspect || '2:3').toString();
      const aspect = `Portrait ${aspectRaw}`;
      const tone = Array.isArray(dz.tone) ? dz.tone.join(', ') : (dz.tone || '');
      const palette = Array.isArray(dz.palette) ? dz.palette.join(', ') : (dz.palette || '');
      const composition = (dz.composition || '').toString();
      const typography = (dz.typography || '').toString();

      const header = `A stunning, ${String(genre || '').trim() || 'book'} book cover.`;
      const lines: string[] = [
        header,
        t ? `Title: ${t}` : '',
        a ? `Author: ${a}` : '',
        s ? `Series: ${s}${sn ? ` (Book ${sn})` : ''}` : '',
        lang ? `Language: ${lang}` : '',
        `Text Direction: ${dir}`,
        `Aspect Ratio: ${aspect}`,
        tone ? `Tone: ${tone}` : '',
        palette ? `Palette: ${palette}` : '',
        composition ? `Composition: ${composition}` : '',
        typography ? `Typography: ${typography}` : '',
        imagery.length ? `Imagery: ${imagery.join(', ')}` : '',
        constraints.length ? `Constraints: ${constraints.join('; ')}` : '',
      ].filter(Boolean);

      // A natural description that weaves key points without copying labels
      const descParts: string[] = [];
      if (composition) descParts.push(composition.replace(/^[A-Z]/, (m: string) => m.toLowerCase()));
      if (imagery.length) descParts.push(`featuring ${imagery.slice(0, 5).join(', ')}`);
      if (tone) descParts.push(`evoking a ${tone} mood`);
      if (palette) descParts.push(`with a ${palette} palette`);
      if (typography) descParts.push(`and typography that ${typography}`);
      const core = descParts.length ? (descParts[0].charAt(0).toUpperCase() + descParts[0].slice(1)) + (descParts.slice(1).length ? ', ' + descParts.slice(1).join(', ') : '') : '';
      const constraintNote = 'Ensure strong contrast and a clean background for thumbnail legibility; avoid watermarks, borders, and marketing badges.';
      const desc = `Description: Create a bookstore-quality, on-market cover in ${aspect.toLowerCase()}, ${core ? core + '.' : ''} ${constraintNote}`.trim();

      return [...lines, '', desc].join('\n');
    } catch {
      return '';
    }
  };

  // Determine title/series per rules (per-field fallback):
  const chosenTitle = (translatedTitleText?.trim() || originalTitleText?.trim() || bookTitle?.trim() || '');
  const chosenSeries = (translatedSeriesText?.trim() || originalSeriesText?.trim() || seriesName?.trim() || '');
  const expectedCoverTitle = mode === 'modify'
    ? ((replaceTitle ? translatedTitleText : originalTitleText)?.trim() || bookTitle?.trim() || '')
    : chosenTitle;
  const expectedCoverSeries = mode === 'modify'
    ? ((replaceSeries ? translatedSeriesText : originalSeriesText)?.trim() || seriesName?.trim() || '')
    : chosenSeries;

  const contextParts: string[] = [];
  if (authorName?.trim()) contextParts.push(`author: ${authorName.trim()}`);
  if (genre?.trim()) contextParts.push(`genre: ${genre.trim()}`);
  const tropesText = asList(tropes);
  if (tropesText) contextParts.push(`tropes: ${tropesText}`);
  const keywordsText = asList(keywords);
  if (keywordsText) contextParts.push(`keywords: ${keywordsText}`);
  const contextStr = contextParts.length ? ` Context: ${contextParts.join('; ')}.` : '';

  // Title/series clause with optional book number and author (if provided)
  let titleSeriesPart = `with the title "${chosenTitle}"`;
  if (chosenSeries) {
    titleSeriesPart += ` and series name "${chosenSeries}"`;
  }
  if (bookNumber != null && String(bookNumber).toString().trim() !== '') {
    titleSeriesPart += ` (Book ${String(bookNumber).toString().trim()})`;
  }
  if (authorName?.trim()) {
    titleSeriesPart += ` by ${authorName.trim()}`;
  }
  titleSeriesPart += '.';

  const defaultPromptText = `Portrait 2:3 bookstore-quality book cover ${titleSeriesPart}${contextStr} Single cover image only; no collages, grids, borders, watermarks, or signatures. Clear hierarchy: large, highly legible title (thumbnail-readable), author secondary${chosenSeries ? ', series tertiary' : ''}. Strong contrast and a clean background behind text. On-market styling for the specified genre and audience. Render all text in ${selectedLanguage}.`;

  // Track previous default to avoid clobbering user edits unless appropriate
  const userTouchedRef = useRef<boolean>(false);
  const lastAssistKeyRef = useRef<string>('');

  const aiAssistDependencyKey = React.useMemo(() => {
    const normalizedAuthor = (authorName || '').trim();
    const normalizedGenre = (genre || '').trim();
    const normalizedBookNumber =
      bookNumber != null ? String(bookNumber).trim() : '';
    const tropesKey = toArray(tropes).join('|');
    const keywordsKey = toArray(keywords).join('|');

    return [
      selectedLanguage,
      chosenTitle,
      chosenSeries,
      normalizedAuthor,
      normalizedGenre,
      normalizedBookNumber,
      tropesKey,
      keywordsKey,
    ].join('||');
  }, [selectedLanguage, chosenTitle, chosenSeries, authorName, genre, bookNumber, tropes, keywords]);

  // Build deterministic prompt from backend when in generate mode or key metadata changes
  useEffect(() => {
    if (!visible || mode !== 'generate') return;
    const controller = new AbortController();

    const doFetch = async () => {
      try {
        const payload = {
          title: chosenTitle || '',
          author: (authorName || '').trim(),
          series_name: chosenSeries || '',
          series_number: bookNumber != null && String(bookNumber).toString().trim() !== '' ? String(bookNumber).toString().trim() : '',
          language: selectedLanguage, // accepts names like "English", "European Portuguese"
          primary_genre: (genre || '').trim(),
          tropes: toArray(tropes),
          keywords: toArray(keywords),
          direction: null,
          aspect: '2:3',
          textRender: 'model',
          use_llm: false, // deterministic default
          prompt_language: 'English',
          bookID: bookId,
          chapterID: 'cover',
        };

        const res = await fetch(`${API_BASE}/api/build-cover-prompt`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        const prompt = (data && data.prompt) || '';
        if (prompt) {
          setDeterministicPrompt(prompt);
          // Do not prefill the Prompt box; it will be filled by AI Assist.
          // Do not change promptWaiting here.
        }
      } catch (_e) {
        // ignore failures; fallback to existing simple defaultPromptText
      }
    };

    doFetch();
    return () => controller.abort();
  }, [
    mode,
    visible,
    chosenTitle,
    chosenSeries,
    authorName,
    selectedLanguage,
    genre,
    tropes,
    keywords,
    bookNumber,
  ]);


  // Suppress AI Assist on language switch from Original -> translated when cover exists
  useEffect(() => {
    if (!visible) return;
    const prev = prevSelectedLangRef.current;
    if (prev !== selectedLanguage) {
      prevSelectedLangRef.current = selectedLanguage;
      if (prev === 'Original' && selectedLanguage !== 'Original' && !!currentCoverUrl) {
        // We are moving from Original -> translated language and have an original cover.
        // We will auto-switch to Translate tab; do not generate AI Assist until user switches back to Generate.
        suppressAiAssistDueToLangSwitchRef.current = true;
      }
    }
  }, [visible, selectedLanguage, currentCoverUrl]);

  // Clear suppression when not on Generate tab, so if user switches back to Generate later it can run
  useEffect(() => {
    if (mode !== 'generate') {
      suppressAiAssistDueToLangSwitchRef.current = false;
    }
  }, [mode]);

  // Auto-trigger AI Assist once when the Generate tab first becomes active per modal open.
  // If opening due to "Regenerate", suppress auto AI Assist and restore prior prompt.
  useEffect(() => {
    if (!visible) {
      // Reset between modal openings
      aiAssistTriggeredRef.current = false;
      lastAssistKeyRef.current = '';
      return;
    }
    if (shouldOpenInModifyMode) {
      setPromptWaiting(false);
      setAiAssistLoading(false);
      return;
    }
    if (mode === 'generate' && !aiAssistTriggeredRef.current) {
      // If we're here due to switching from Original -> translated with a cover, do not auto-run AI Assist
      if (suppressAiAssistDueToLangSwitchRef.current) {
        // Reveal the prompt editor immediately without triggering AI Assist
        setPromptWaiting(false);
        return;
      }
      aiAssistTriggeredRef.current = true;

      let suppress = false;
      try {
        suppress = sessionStorage.getItem('poeai:suppressCoverAutoAiAssist') === '1';
      } catch (_e) {}

      if (suppress) {
        try {
          sessionStorage.removeItem('poeai:suppressCoverAutoAiAssist');
        } catch (_e) {}
        // Restore previously used prompt if available
        try {
          const key = `poeai:lastCoverPrompt:${bookId}:${selectedLanguage}`;
          const prev = sessionStorage.getItem(key);
          if (prev && prev.trim()) {
            userTouchedRef.current = true;
            setCustomPrompt(prev);
          }
        } catch (_e) {}
        // Do not auto-run AI Assist; reveal the prompt editor immediately
        setPromptWaiting(false);
        lastAssistKeyRef.current = aiAssistDependencyKey;
      } else {
        setPromptWaiting(true);
        lastAssistKeyRef.current = aiAssistDependencyKey;
        // Fire-and-forget; handleAiAssist handles its own loading, success, and fallback
        void handleAiAssist(true, aiAssistDependencyKey);
      }
    }
  }, [visible, mode, bookId, selectedLanguage, aiAssistDependencyKey, shouldOpenInModifyMode]);

  // Build AI-assisted prompt using backend helper with optional LLM
  const handleAiAssist = async (quiet: boolean = false, triggerKey?: string): Promise<void> => {
    if (mode !== 'generate') return;
    const runKey = triggerKey ?? aiAssistDependencyKey;
    if (runKey) {
      lastAssistKeyRef.current = runKey;
    }
    setAiAssistLoading(true);
    try {
      const payload = {
        title: chosenTitle || '',
        author: (authorName || '').trim(),
        series_name: chosenSeries || '',
        series_number: bookNumber != null && String(bookNumber).toString().trim() !== '' ? String(bookNumber).toString().trim() : '',
        language: selectedLanguage,
        primary_genre: (genre || '').trim(),
        tropes: toArray(tropes),
        keywords: toArray(keywords),
        direction: null,
        aspect: '2:3',
        textRender: 'model',
        use_llm: true,
        prompt_language: 'English',
        bookID: bookId,
        chapterID: 'cover',
      };

      const res = await fetch(`${API_BASE}/api/build-cover-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || 'Failed to build prompt');
      }
      const data = await res.json();
      // Prefer structured summary and use it to build the final prompt
      const structured = (data && (data as any).ai_structured) || null;
      let prompt = '';
      if (structured) {
        const composed = buildPromptFromSummary(structured);
        prompt = composed || ((data && data.prompt) || '');
      } else {
        // fallback to backend prompt
        prompt = (data && data.prompt) || '';
      }

      if (!prompt) {
        throw new Error('No prompt returned');
      }

      if (runKey && lastAssistKeyRef.current !== runKey) {
        return;
      }

      if (structured) {
        setAiAssistBreakdown(structured);
      } else {
        setAiAssistBreakdown(null);
      }
      userTouchedRef.current = true;
      setCustomPrompt(prompt);
      setPromptWaiting(false);
      if (!quiet) {
        message.success('AI Assist prompt applied');
      }
    } catch (e: any) {
      if (runKey && lastAssistKeyRef.current !== runKey) {
        return;
      }
      if (!quiet) {
        message.error(`AI Assist failed: ${e?.message || String(e)}`);
      }
      setPromptWaiting(false);
      setAiAssistBreakdown(null);
    } finally {
      if (!runKey || lastAssistKeyRef.current === runKey) {
        setAiAssistLoading(false);
      }
    }
  };

  // Re-run AI Assist when the key inputs for the prompt change while on Generate tab (quietly)
  useEffect(() => {
    if (!visible || mode !== 'generate') return;
    if (suppressAiAssistDueToLangSwitchRef.current) return;
    if (!aiAssistTriggeredRef.current) return;

    const key = aiAssistDependencyKey;
    if (lastAssistKeyRef.current === key) {
      return;
    }

    lastAssistKeyRef.current = key;
    setPromptWaiting(true);
    void handleAiAssist(true, key);
  }, [visible, mode, aiAssistDependencyKey]);

  const handleGenerate = async (): Promise<void> => {
    // Require a prompt in Generate mode
    if (mode === 'generate') {
      const trimmed = (customPrompt || '').trim();
      if (!trimmed) {
        message.error('Enter a prompt or use AI Assist before generating.');
        return;
      }
    }
    if (mode === 'modify' && additionalReplacementsError) {
      message.error(additionalReplacementsError);
      return;
    }
    // Validate for modify mode
    if (mode === 'modify') {
      if (!replaceTitle && !replaceSeries && !addLanguageSticker && !additionalReplacements.length) {
        message.error(`Select what to change: title, series, or add a [${displayLanguage}] Edition sticker.`);
        return;
      }
      if (!rightsConfirmed) {
        message.error('Please confirm you have rights to modify the cover');
        return;
      }
      // Strengthened field validation when replacement options are selected
      if (replaceTitle) {
        const a = (originalTitleText || '').trim();
        const b = (translatedTitleText || '').trim();
        if (!a || !b) {
          message.error('Provide both original and translated titles.');
          return;
        }
        if (a === b) {
          message.error('Original and translated titles must be different.');
          return;
        }
      }
      if (replaceSeries) {
        const a = (originalSeriesText || '').trim();
        const b = (translatedSeriesText || '').trim();
        if (!a || !b) {
          message.error('Provide both original and translated series names.');
          return;
        }
        if (a === b) {
          message.error('Original and translated series names must be different.');
          return;
        }
      }
    }

    if (!hasEnoughCredits) {
      message.error('You do not have enough credits to generate these images.');
      return;
    }

    // Persist per-language cover context for selection modal
    try {
      const normalizedLanguage =
        ((selectedLanguage === 'Original' ? 'original' : selectedLanguage) || 'original').toLowerCase();
      const storageKey = `poeai:coverContext:${bookId}:${normalizedLanguage}`;
      const prevRaw = sessionStorage.getItem(storageKey);
      const prev = prevRaw ? JSON.parse(prevRaw) : {};
      const mergedOptions = {
        ...(prev?.generationOptions || {}),
        mode,
        authorMode: includeAuthor && canonicalAuthorName ? 'include' : 'omit',
        additionalReplacements: mode === 'modify' ? additionalReplacements : [],
        ...(mode === 'modify' ? { replaceTitle, replaceSeries, addLanguageSticker } : {}),
      };
      const ctx = {
        bookTitle: (originalTitleText || bookTitle || '').trim(),
        translatedTitle: (translatedTitleText || '').trim() || undefined,
        seriesName: (originalSeriesText || seriesName || '').trim() || undefined,
        translatedSeriesName: (translatedSeriesText || '').trim() || undefined,
        generationOptions: Object.keys(mergedOptions).length ? mergedOptions : undefined,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(ctx));
    } catch (_e) {}
    onClose();
    onGenerationStart(selectedLanguage);

    try {
      const authHeaders = await getAuthHeaders();
      try {
        if (mode === 'modify' && currentCoverUrl) {
          fetch(`${API_BASE}/api/log-cover-rights`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders },
              body: JSON.stringify({
                bookId,
                userId,
                language: selectedLanguage,
                timestamp: new Date().toISOString(),
                coverUrl: currentCoverUrl,
                targetLanguage: selectedLanguage,
                rightsConfirmed: true,
              }),
          }).catch(() => {});
        }
      } catch (_e) {
        // ignore logging failures
      }

      const textModifications = mode === 'modify'
        ? {
            replaceTitle,
            replaceSeries,
            addLanguageSticker,
            ...(replaceTitle
              ? {
                  originalTitle: originalTitleText,
                  translatedTitle: translatedTitleText,
                }
              : {}),
            ...(replaceSeries
              ? {
                  originalSeries: originalSeriesText,
                  translatedSeries: translatedSeriesText,
                }
              : {}),
            targetLanguage: selectedLanguage,
            authorMode: includeAuthor && canonicalAuthorName ? 'include' : 'omit',
            additionalReplacements,
            userGuidance: normalizedCoverTranslationGuidance.trim() || undefined,
          }
        : null;

      const finalPrompt = (customPrompt || '').trim();
      // Persist the last-used prompt so "Regenerate" can restore exactly what the user used
      try {
        const key = `poeai:lastCoverPrompt:${bookId}:${selectedLanguage}`;
        sessionStorage.setItem(key, finalPrompt);
      } catch (_e) {}

      const { provider: submissionProvider, model: submissionModel } = resolveModelSelection(
        mode === 'generate' ? generateModel : provider
      );

      const response = await fetch(`${API_BASE}/api/generate-cover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            mode,
            customPrompt: finalPrompt,
            userId,
            bookId,
            language: selectedLanguage,
            client_estimated_cost: coverCreditCost,
            coverText: {
              title: expectedCoverTitle,
              series: expectedCoverSeries || undefined,
              authorMode: includeAuthor && canonicalAuthorName ? 'include' : 'omit',
            },
            ...(mode === 'modify' ? { textModifications } : {}),
            provider: submissionProvider,
            model: submissionModel,
            n: imageCount,
            quality: 'high',
            response_format: 'b64_json',
            ...(mode === 'modify' && currentCoverUrl
              ? {
                  reference_images: [currentCoverUrl],
                  input_fidelity: 'high',
                }
              : {}),
        }),
      });

      let data: GenerateCoverResponse | any = null;
      let parsedOk = true;
      try {
        data = await response.json();
      } catch (_e) {
        parsedOk = false;
      }
      if (!response.ok) {
        const errMsg =
          (data && (data as any).detail) ||
          (data && (data as any).message) ||
          response.statusText ||
          'Request failed';
        message.error('Failed to generate cover: ' + errMsg);
        onGenerationComplete([]);
        return;
      }
      if (!parsedOk || !data) {
        message.error('Unexpected server response while generating the cover. Please try again.');
        onGenerationComplete([]);
        return;
      }

      if (data.status === 'success') {
        const resultsArray = Array.isArray(data.result)
          ? data.result
          : [data.result];

        const validImages = resultsArray.filter((img: any) => img && img.b64_json);
        if (validImages.length === 0) {
          const diagText =
            resultsArray.length === 1 && (resultsArray[0] as any)?.content
              ? (resultsArray[0] as any).content
              : undefined;
          message.error({
            content: (
              <div>
                <p>We couldn’t generate an image this time.</p>
                {diagText && (
                  <p className="cover-refresh-message-detail">
                    {diagText}
                  </p>
                )}
              </div>
            ),
            duration: 6,
          });
          onGenerationComplete([]);
          return;
        }

        const images = validImages.map((img: any) => imageDataUrlFromBase64(img.b64_json));

        const req = (data as any).requested ?? imageCount;
        const gen = (data as any).generated ?? validImages.length;
        const partialFailure = (data as any).partial_failure || (gen < req);
        if (partialFailure) {
          message.warning(`Generated ${gen} of ${req} image${req > 1 ? 's' : ''}. Credits were charged only for successful generations.`);
        }

        if ((data as any).credit_charge_status === 'failed') {
          message.warning('Images generated, but we couldn’t charge credits right now. Your balance may update shortly.');
        }

        onGenerationComplete(images, {
          mode,
          replaceTitle: mode === 'modify' ? replaceTitle : undefined,
          replaceSeries: mode === 'modify' ? replaceSeries : undefined,
          addLanguageSticker: mode === 'modify' ? addLanguageSticker : undefined,
          authorMode: includeAuthor && canonicalAuthorName ? 'include' : 'omit',
          additionalReplacements: mode === 'modify' ? additionalReplacements : [],
          originalTitle: originalTitleText,
          translatedTitle: translatedTitleText,
          originalSeries: originalSeriesText,
          translatedSeries: translatedSeriesText,
          language: selectedLanguage,
          // New: provide counts so selection modal can show partial-failure banner
          requestedCount: req,
          generatedCount: gen,
          partialFailure,
        });
      } else if (data.status === 'moderation_error') {
        message.error({
          content: (
            <div>
              <p>{data.message}</p>
              <p className="cover-refresh-message-detail">
                {data.suggestion}
              </p>
            </div>
          ),
          duration: 8,
        });
        onGenerationComplete([]);
        return;
      } else if (data.status === 'error') {
        const msg = data.message || 'Failed to generate cover';
        if (/insufficient credits/i.test(msg)) {
          message.error('Insufficient credits. Reduce the number of images or purchase more credits.');
        } else {
          message.error(msg);
        }
        onGenerationComplete([]);
        return;
      } else {
        // Should never happen, but catch any unexpected status
        throw new Error('Unexpected response status: ' + (data as any).status);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error
        ? error.message
        : String(error);
      message.error('Error generating images: ' + errorMessage);
      onGenerationComplete([]);
    }
  };

  const handleProtectedClose = (): void => {
    if (aiAssistLoading) return;
    onClose();
  };

  const isGenerateDisabled =
    (mode === 'modify' && Boolean(additionalReplacementsError)) ||
    (mode === 'modify' &&
      (
        !rightsConfirmed ||
        (!replaceTitle && !replaceSeries && !addLanguageSticker && !additionalReplacements.length) ||
        // Strengthened field validation when specific replacements are selected
        (replaceTitle && (
          !(originalTitleText?.trim()) ||
          !(translatedTitleText?.trim()) ||
          originalTitleText.trim() === translatedTitleText.trim()
        )) ||
        (replaceSeries && (
          !(originalSeriesText?.trim()) ||
          !(translatedSeriesText?.trim()) ||
          originalSeriesText.trim() === translatedSeriesText.trim()
        ))
      )
    ) ||
    (mode === 'generate' && !(customPrompt?.trim())) ||
    !hasEnoughCredits;

  const disabledReasons: string[] = [];
  if (mode === 'modify' && additionalReplacementsError) disabledReasons.push(additionalReplacementsError);
  if (mode === 'modify') {
    if (!rightsConfirmed) {
      disabledReasons.push('Confirm you have the rights to modify this cover.');
    }
    if (!replaceTitle && !replaceSeries && !addLanguageSticker && !additionalReplacements.length) {
      disabledReasons.push(`Select what to change: title, series, or add a [${displayLanguage}] Edition sticker.`);
    }
    if (replaceTitle) {
      if (!originalTitleText?.trim() || !translatedTitleText?.trim()) {
        disabledReasons.push('Provide both original and translated titles.');
      } else if (originalTitleText.trim() === translatedTitleText.trim()) {
        disabledReasons.push('Original and translated titles must be different.');
      }
    }
    if (replaceSeries) {
      if (!originalSeriesText?.trim() || !translatedSeriesText?.trim()) {
        disabledReasons.push('Provide both original and translated series names.');
      } else if (originalSeriesText.trim() === translatedSeriesText.trim()) {
        disabledReasons.push('Original and translated series names must be different.');
      }
    }
  }
  if (mode === 'generate') {
    if (!(customPrompt?.trim())) {
      disabledReasons.push('Enter a prompt or use AI Assist to build one.');
    }
    if (aiAssistLoading) {
      disabledReasons.push('Please wait for AI Assist to finish building the prompt.');
    }
  }
  if (isCreditInfoLoading) {
    disabledReasons.push('Credit balance is still loading.');
  } else if (!hasEnoughCredits) {
    const available = creditBalanceValue ?? 0;
    disabledReasons.push(
      `Insufficient credits. Need ${coverCreditCost}, available ${available}. Reduce the number of images or purchase more credits.`
    );
  }

  // Inline validation flags for modify inputs
  const titleInputsMissing = mode === 'modify' && replaceTitle && (!originalTitleText?.trim() || !translatedTitleText?.trim());
  const titleInputsSame = mode === 'modify' && replaceTitle && (!!originalTitleText?.trim() && !!translatedTitleText?.trim() && originalTitleText.trim() === translatedTitleText.trim());
  const seriesInputsMissing = mode === 'modify' && replaceSeries && (!originalSeriesText?.trim() || !translatedSeriesText?.trim());
  const seriesInputsSame = mode === 'modify' && replaceSeries && (!!originalSeriesText?.trim() && !!translatedSeriesText?.trim() && originalSeriesText.trim() === translatedSeriesText.trim());

  // Tooltip reason for when Modify Existing Cover is disabled
  const isOriginalLanguage = selectedLanguage === 'Original';
  const modifyDisabled = !currentCoverUrl || isOriginalLanguage;
  const modifyDisabledReason = isOriginalLanguage
    ? 'Translate Existing Cover is unavailable when using the book’s original language. Select a different language to enable translation.'
    : 'Translate Existing Cover is unavailable because no original cover image was found for this book.';

  // ------------- TOUR: refs and state -------------
  const langRef = useRef<HTMLDivElement | null>(null);
  const modeRef = useRef<HTMLDivElement | null>(null);
  const providerRef = useRef<HTMLDivElement | null>(null);
  const rightsRef = useRef<HTMLDivElement | null>(null);
  const modsRef = useRef<HTMLDivElement | null>(null);
  const promptHeaderRef = useRef<HTMLDivElement | null>(null);
  const promptRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const creditsRef = useRef<HTMLDivElement | null>(null);
  const countRef = useRef<HTMLDivElement | null>(null);
  const noteRef = useRef<HTMLDivElement | null>(null);
  const generateBtnRef = useRef<HTMLButtonElement | null>(null);

  const [tourOpen, setTourOpen] = useState(false);
  const [tourKind, setTourKind] = useState<'generate' | 'translate'>('generate');
  const [flagsFetched, setFlagsFetched] = useState(false);
  const [allowAutoOpen, setAllowAutoOpen] = useState(false);

  // Ensure Tour opens only after key targets exist in the DOM
  const ensureTourOpen = (kind: 'generate' | 'translate') => {
    setTourKind(kind);
    let attempts = 0;
    const checkReady = () => {
      const ready = kind === 'generate'
        ? !!(langRef.current && modeRef.current && (promptHeaderRef.current || promptRef.current) && generateBtnRef.current)
        : !!(langRef.current && modeRef.current && providerRef.current && rightsRef.current && modsRef.current && generateBtnRef.current);
      if (ready || attempts >= 10) {
        setTourOpen(true);
      } else {
        attempts += 1;
        setTimeout(checkReady, 50);
      }
    };
    setTimeout(checkReady, 0);
  };

  // Fetch server-side UI flags for tours; decide if we should auto-open once
  useEffect(() => {
    if (!visible) {
      setTourOpen(false);
      setFlagsFetched(false);
      setAllowAutoOpen(false);
      return;
    }
    if (!userId) return;

    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/user/ui-flags?userId=${encodeURIComponent(userId)}`, {
          headers: await getAuthHeaders(),
        });
        let seen = false;
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          const flags = data?.uiFlags || {};
          const flagName = mode === 'generate' ? 'coverGenTourSeenGenerate' : 'coverGenTourSeenTranslate';
          seen = !!flags[flagName];
        }
        if (mounted) {
          setAllowAutoOpen(!seen);
        }
      } catch (_e) {
        // Non-blocking
      } finally {
        if (mounted) setFlagsFetched(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [visible, userId, mode]);

  // Auto-open tour when conditions are ready
  useEffect(() => {
    if (!visible || !flagsFetched || !allowAutoOpen) return;
    const kind: 'generate' | 'translate' = mode === 'generate' ? 'generate' : 'translate';
    ensureTourOpen(kind);
    setAllowAutoOpen(false);
  }, [visible, flagsFetched, allowAutoOpen, mode]);

  const handleCloseTour = async () => {
    setTourOpen(false);
    if (!userId) return;
    const flagName = tourKind === 'generate' ? 'coverGenTourSeenGenerate' : 'coverGenTourSeenTranslate';
    try {
      await fetch(`${API_BASE}/user/ui-flags/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({
          userId,
          flags: { [flagName]: true },
        }),
      });
    } catch (_e) {
      // Non-blocking
    }
  };

  const openTourManually = () => {
    ensureTourOpen(mode === 'generate' ? 'generate' : 'translate');
  };

  const mkStep = (refEl: React.RefObject<HTMLElement>, title: string, description: React.ReactNode, placement?: any) => {
    if (!refEl?.current) return null;
    return { title, description, target: refEl.current, placement };
  };

const generateSteps = [
  mkStep(
    langRef,
    'Language for on-cover text',
    'Pick the language the AI should render on the cover (title, author, series).'
  ),
  mkStep(
    modeRef,
    'Pick a mode',
    'Switch between translating an existing cover or generating a brand-new one from a prompt. Translate is disabled until you choose a non-original language with an existing cover.'
  ),
  mkStep(
    promptHeaderRef,
    'AI Assist',
    'Builds a market-aware prompt from your title, series, blurb, genres, tropes, and keywords. You can edit the prompt before generating.'
  ),
  mkStep(
    promptRef,
    'Your prompt',
    'Refine the vibe: mention genre cues, mood, setting, color palette, lighting. Short, concrete directions work best.'
  ),
  mkStep(
    tipRef,
    'Pro tip',
    'Make sure to fill in your Genre, Tropes, and Keywords in your book’s metadata. AI Assist uses them to shape on-market designs.'
  ),
mkStep(
creditsRef,
'Credits & cost',
<>We’ve priced cover generation with <strong>iteration in mind.</strong> Most books take a few rounds to nail typography and layout. See your balance and the estimated total for this run. You’re only charged for successful generations.</>
),
  mkStep(
    countRef,
    'How many images',
    'Choose how many options to create in one go.'
  ),
  mkStep(
    noteRef,
    'Heads-up',
    'It can take a few tries to land the perfect cover, especially with decorative lettering or complex typefaces. Iteration is expected.'
  ),
  mkStep(
    generateBtnRef as any,
    'Generate images',
    'Create your images. If the button is disabled, review the blocker list in the modal. After launch, progress and any errors appear in the next cover review screen.'
  ),
].filter(Boolean) as any[];

const translateSteps = [
  mkStep(
    langRef,
    'Choose a translation language',
    'Pick the target language for the on-cover text. Translate mode is unavailable for the original language.'
  ),
  mkStep(
    modeRef,
    'Translate existing cover',
    'Swap on-cover text into the selected language while preserving the artwork.'
  ),
  mkStep(
    providerRef,
    'Choose a model',
    'Pick which AI model to run the translation with. Results can vary by model.'
  ),
  mkStep(
    rightsRef,
    'Confirm rights',
    'Confirm you’re allowed to modify this cover. This gates generation because translated cover text changes an existing image asset.'
  ),
  mkStep(
    modsRef,
    'What to change',
    <>
      Select which text to replace (title and/or series). You can also add a
      {' '}[{displayLanguage} Edition] sticker. Stickers can affect layout and may need extra iterations. Original and translated text must differ before generation can start.
    </>
  ),
  mkStep(
    creditsRef,
    'Credits & Cost',
<>We’ve priced cover generation with <strong>iteration in mind.</strong> Most books take a few rounds to nail typography and layout. See your balance and the estimated total for this run. You’re only charged for successful generations.</>
),
  mkStep(
    countRef,
    'How many variants',
    'Choose how many translated image variants to produce in one go.'
  ),
  mkStep(
    noteRef,
    'Heads-up',
    'It can take a few tries to get clean typography, especially with complex fonts or extra on-cover text.'
  ),
  mkStep(
    generateBtnRef as any,
    'Generate',
    'Create your images. If the button is disabled, review the blocker list in the modal. After launch, progress and any errors appear in the next cover review screen.'
  ),
].filter(Boolean) as any[];

  const steps = tourKind === 'generate' ? generateSteps : translateSteps;
  const activeModelSelection = mode === 'generate' ? generateModel : provider;
  const activeModelOption = imageModelOptions.find(
    model => model.id === activeModelSelection || model.apiModel === activeModelSelection
  );
  const activeModelLabel = activeModelOption?.displayName || activeModelSelection;
  const visibleRunContext = [
    { label: 'Target', value: displayLanguage },
    {
      label: 'Author',
      value: includeAuthor && canonicalAuthorName ? canonicalAuthorName : 'Not included',
    },
    { label: 'Mode', value: mode === 'modify' ? 'Translate existing cover' : 'Generate new cover' },
    { label: 'Output', value: `${imageCount} image${imageCount > 1 ? 's' : ''}` },
    { label: 'Credit cost', value: `${coverCreditCost.toLocaleString()} credits` },
    {
      label: 'Selected result',
      value: mode === 'modify'
        ? 'Chosen output replaces the localized cover after review'
        : 'Chosen output becomes the cover after review',
    },
  ];
  const handleModelSelectionChange = (value: CoverModelSelection) => {
    if (mode === 'generate') {
      setGenerateModel(value);
      return;
    }
    setProvider(value);
  };
  const numericCreditBalance = (() => {
    if (typeof creditBalanceValue === 'number' && Number.isFinite(creditBalanceValue)) {
      return creditBalanceValue;
    }
    if (typeof creditBalanceValue === 'string') {
      const parsed = Number(creditBalanceValue);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  })();
  const primaryFooterBlocker = disabledReasons[0] || '';
  const coverCostRows = React.useMemo<ModalCostBreakdownRow[]>(() => {
    const rows: ModalCostBreakdownRow[] = [
      {
        key: 'cover-images',
        label: mode === 'modify' ? 'Translate existing cover' : 'Generate new cover',
        amountCredits: coverCreditCost,
        explanation: `${imageCount} image${imageCount > 1 ? 's' : ''} with ${activeModelLabel}`,
      },
    ];

    if (mode === 'modify') {
      const replacementItems = [
        replaceTitle ? 'Title' : '',
        replaceSeries ? 'Series name' : '',
        addLanguageSticker ? 'Edition sticker' : '',
        includeAuthor && canonicalAuthorName ? `Author: ${canonicalAuthorName}` : 'No author name',
      ].filter(Boolean);
      rows.push({
        key: 'cover-replacements',
        label: 'Selected text changes',
        displayValue: replacementItems.length ? replacementItems.join(', ') : 'None selected',
        tone: replacementItems.length ? 'info' : 'warning',
      });
    }

    return rows;
  }, [
    activeModelLabel,
    addLanguageSticker,
    coverCreditCost,
    imageCount,
    mode,
    replaceSeries,
    replaceTitle,
    includeAuthor,
    canonicalAuthorName,
  ]);
  const footerCostSummary = (
    <div ref={noteRef} className="translation-modal-footer-summary cover-refresh-footer-summary" aria-live="polite">
      <span>
        <strong>{formatCredits(coverCreditCost)} credits</strong> required
      </span>
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>
        <strong>{numericCreditBalance == null ? 'Loading...' : formatCredits(numericCreditBalance)}</strong>{' '}
        available
      </span>
      {!hasEnoughCredits && !isCreditInfoLoading ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="translation-modal-footer-summary__warning">Top-up needed</span>
        </>
      ) : null}
      {primaryFooterBlocker ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="cover-refresh-footer-blocker">Action needed: {primaryFooterBlocker}</span>
        </>
      ) : null}
      <Button
        type="link"
        size="small"
	        className="translation-modal-footer-summary__details"
	        aria-label="View cost breakdown"
	        onClick={() => setIsCostBreakdownModalVisible(true)}
	        data-testid="tutorial:cover:cost-breakdown-entry"
	      >
        View details
      </Button>
    </div>
  );

  return (
    <>
    <Modal
      className="cover-generation-modal"
      title={
        <div className="cover-refresh-titlebar">
          <span className="cover-refresh-title">Generate Cover for {displayLanguage}</span>
          <Button type="link" size="small" icon={<QuestionCircleOutlined />} onClick={openTourManually} className="app-tour-trigger cover-refresh-titlebar__tour">
            Take a tour
          </Button>
        </div>
      }
      visible={visible}
      onCancel={handleProtectedClose}
      closable={!aiAssistLoading}
      maskClosable={!aiAssistLoading}
      keyboard={!aiAssistLoading}
      width="min(880px, calc(100vw - 32px))"
      centered
      footer={
        <div className="cover-refresh-footer" data-testid="tutorial:cover:footer">
          {footerCostSummary}
          <div className="cover-refresh-footer-actions">
            <Button key="cancel" onClick={handleProtectedClose} disabled={aiAssistLoading}>
              Cancel
            </Button>
            {isGenerateDisabled ? (
              <Tooltip
                key="generate-tip"
                title="Resolve the blockers listed in the modal."
              >
                <span>
                  <Button key="generate" type="primary" onClick={handleGenerate} ref={generateBtnRef} disabled data-testid="tutorial:cover:generate">
                    Generate {imageCount} Image{imageCount > 1 ? 's' : ''}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button key="generate" type="primary" onClick={handleGenerate} ref={generateBtnRef} disabled={aiAssistLoading || imageModelOptionsLoading || !activeModelOption || Boolean(activeModelOption.enabledModes?.length && !activeModelOption.enabledModes.includes(mode))} data-testid="tutorial:cover:generate">
                Generate {imageCount} Image{imageCount > 1 ? 's' : ''}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="cover-refresh-body" data-testid="tutorial:cover:modal">
      <div className="cover-refresh-modal-stage" data-testid="tutorial:cover:stage">
        {mode === 'generate' && aiAssistLoading && (
          <div className="cover-refresh-loading-overlay" role="status" aria-live="polite">
            <div className="cover-refresh-loading-card">
            <Spin
              size="large"
              indicator={<FeatherSpinner style={{ width: 128, height: 128 }} />}
            />
              <div className="cover-refresh-loading-title">Building cover prompt</div>
              <p className="cover-refresh-loading-copy">
                Reviewing title, author, genre, tropes, and keywords for {displayLanguage}.
              </p>
            </div>
          </div>
        )}
        <div className={mode === 'generate' && aiAssistLoading ? 'cover-refresh-workflow is-loading' : 'cover-refresh-workflow'}>
          <section className="cover-refresh-block cover-refresh-block--setup" aria-labelledby="cover-generation-setup-heading" data-testid="tutorial:cover:setup">
            <div className="cover-refresh-block-header">
              <div>
                <h3 id="cover-generation-setup-heading">Cover setup</h3>
                <p>{mode === 'modify' ? 'Translate on-cover text while preserving artwork.' : 'Generate new cover concepts from your prompt.'}</p>
              </div>
            </div>
            <div className="cover-refresh-setup-grid">
              <div ref={langRef} className="cover-refresh-control-tile" data-testid="tutorial:cover:language">
                <div className="cover-refresh-label">Language</div>
                <Select
                  value={selectedLanguage}
                  onChange={(v: string) => setSelectedLanguage(v)}
                  style={{ width: '100%' }}
                >
                  {languageOptions.map((lang) => (
                    <Select.Option key={lang} value={lang} disabled={lang === 'Hebrew'}>
                      {lang === 'Original' ? (bookLanguageName || 'Original') : lang}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div ref={modeRef} className="cover-refresh-control-tile cover-refresh-control-tile--wide" data-testid="tutorial:cover:mode">
                <div className="cover-refresh-label">Mode</div>
                <Select
                  value={mode}
                  onChange={(value: 'modify' | 'generate') => setMode(value)}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="modify" disabled={modifyDisabled} title={modifyDisabled ? modifyDisabledReason : undefined}>
                    Translate Existing Cover
                  </Select.Option>
                  <Select.Option value="generate">
                    Generate New Cover
                  </Select.Option>
                </Select>
              </div>
              <div ref={providerRef} className="cover-refresh-control-tile" data-testid="tutorial:cover:model">
                <div className="cover-refresh-label">Model</div>
                <Select
                  value={activeModelSelection}
                  onChange={(value: CoverModelSelection) => handleModelSelectionChange(value)}
                  loading={imageModelOptionsLoading}
                  style={{ width: '100%' }}
                  className="cover-model-select"
                  aria-label="Cover image model"
                  popupClassName="cover-model-dropdown"
                  dropdownMatchSelectWidth={320}
                  dropdownAlign={{ overflow: { adjustX: true, adjustY: true, shiftX: true }, htmlRegion: 'visible' }}
                  optionLabelProp="selectedLabel"
                  options={coverModelOptions(imageModelOptions, model => Boolean(model.enabledModes?.length && !model.enabledModes.includes(mode)))}
                />
              </div>
              <div ref={countRef} className="cover-refresh-control-tile" data-testid="tutorial:cover:image-count">
                <div className="cover-refresh-label">Images</div>
                <Select
                  value={imageCount}
                  onChange={(value: number | string) => setImageCount(Number(value))}
                  style={{ width: '100%' }}
                >
                  <Select.Option value={1}>1 image</Select.Option>
                  <Select.Option value={2}>2 images</Select.Option>
                  <Select.Option value={3}>3 images</Select.Option>
                </Select>
              </div>
            </div>
          </section>

          <div className="cover-refresh-layout">
            <div className="cover-refresh-main">
              {mode === 'modify' && currentCoverUrl && (
                <>
                  <section ref={modsRef} className="cover-refresh-block" data-testid="tutorial:cover:text-replacements">
                    <div className="cover-refresh-block-header">
                      <div>
                        <h3>Text replacements</h3>
                        <p>Select at least one cover element to change.</p>
                      </div>
                    </div>
                    <div className="cover-refresh-choice-stack">
                      <div className="cover-refresh-choice-row">
                        <Checkbox
                          checked={replaceTitle}
                          onChange={e => setReplaceTitle(e.target.checked)}
                        >
                          Replace title with translated title
                        </Checkbox>
                        {replaceTitle && (
                          <div className="cover-refresh-replacement-fields">
                            <div className="cover-refresh-replacement-grid">
                              <Input
                                size="small"
                                value={originalTitleText}
                                onChange={e => {
                                  replacementFieldsEditedRef.current.originalTitle = true;
                                  setOriginalTitleText(e.target.value);
                                }}
                                placeholder="Original title"
                              />
                              <span className="cover-refresh-replacement-arrow">→</span>
                              <Input
                                size="small"
                                value={translatedTitleText}
                                onChange={e => {
                                  replacementFieldsEditedRef.current.translatedTitle = true;
                                  setTranslatedTitleText(e.target.value);
                                }}
                                placeholder={`Translated title (${displayLanguage})`}
                              />
                            </div>
                          </div>
                        )}
                        {replaceTitle && (titleInputsMissing || titleInputsSame) && (
                          <div className="cover-refresh-field-error">
                            {titleInputsMissing ? 'Provide both original and translated titles.' : 'Original and translated titles must be different.'}
                          </div>
                        )}
                      </div>

                      <div className="cover-refresh-choice-row">
                        <Checkbox
                          checked={replaceSeries}
                          onChange={e => setReplaceSeries(e.target.checked)}
                          disabled={!hasSeriesName}
                          title={!hasSeriesName ? 'No series name on this book' : undefined}
                        >
                          Replace series name with translated series name
                        </Checkbox>
                        {replaceSeries && (
                          <div className="cover-refresh-replacement-fields">
                            <div className="cover-refresh-replacement-grid">
                              <Input
                                size="small"
                                value={originalSeriesText}
                                onChange={e => {
                                  replacementFieldsEditedRef.current.originalSeries = true;
                                  setOriginalSeriesText(e.target.value);
                                }}
                                placeholder="Original series"
                              />
                              <span className="cover-refresh-replacement-arrow">→</span>
                              <Input
                                size="small"
                                value={translatedSeriesText}
                                onChange={e => {
                                  replacementFieldsEditedRef.current.translatedSeries = true;
                                  setTranslatedSeriesText(e.target.value);
                                }}
                                placeholder={`Translated series (${displayLanguage})`}
                              />
                            </div>
                          </div>
                        )}
                        {replaceSeries && (seriesInputsMissing || seriesInputsSame) && (
                          <div className="cover-refresh-field-error">
                            {seriesInputsMissing ? 'Provide both original and translated series names.' : 'Original and translated series names must be different.'}
                          </div>
                        )}
                      </div>

                      <div className="cover-refresh-choice-row cover-refresh-choice-row--compact">
                        <Checkbox
                          checked={includeAuthor}
                          onChange={e => setIncludeAuthor(e.target.checked)}
                          disabled={!canonicalAuthorName}
                        >
                          {canonicalAuthorName
                            ? `Include author name: ${canonicalAuthorName}`
                            : 'Include author name (set an author in book metadata first)'}
                        </Checkbox>
                      </div>

                      <div className="cover-refresh-choice-row cover-refresh-choice-row--compact">
                        <Checkbox
                          checked={addLanguageSticker}
                          onChange={e => setAddLanguageSticker(e.target.checked)}
                        >
                          <Tooltip title={`This "[${displayLanguage} Edition]" sticker will be translated into ${displayLanguage}.`}>
                            Add [{displayLanguage} Edition] sticker
                          </Tooltip>
                        </Checkbox>
                      </div>
                    </div>
                    <div className="cover-refresh-guidance-field">
                      <div className="cover-refresh-label">Additional text replacements</div>
                      <p>Replace other on-cover text in place, such as individual titles in a boxset. Up to 10 rows.</p>
                      {additionalReplacements.map((row, index) => (
                        <div key={index} className="cover-refresh-extra-replacement-row">
                          <Input aria-label={`Original text ${index + 1}`} placeholder="Original text on cover" maxLength={200}
                            value={row.source} onChange={e => setAdditionalReplacements(rows => rows.map((item, i) => i === index ? { ...item, source: e.target.value } : item))} />
                          <span aria-hidden="true">→</span>
                          <Input aria-label={`Replacement text ${index + 1}`} placeholder="Replacement text" maxLength={200}
                            value={row.replacement} onChange={e => setAdditionalReplacements(rows => rows.map((item, i) => i === index ? { ...item, replacement: e.target.value } : item))} />
                          <Button aria-label={`Remove replacement ${index + 1}`} onClick={() => setAdditionalReplacements(rows => rows.filter((_, i) => i !== index))}>Remove</Button>
                        </div>
                      ))}
                      <Button disabled={additionalReplacements.length >= 10} onClick={() => setAdditionalReplacements(rows => [...rows, { source: '', replacement: '' }])}>Add text replacement</Button>
                      {additionalReplacementsError && <div className="cover-refresh-field-error" role="alert">{additionalReplacementsError}</div>}
                    </div>
                    <div className="cover-refresh-guidance-field">
                      <div className="cover-refresh-field-heading">
                        <div className="cover-refresh-label">Cover edit guidance</div>
                        <span className="cover-refresh-character-count">
                          {normalizedCoverTranslationGuidance.length}/{MAX_COVER_TRANSLATION_GUIDANCE_CHARS}
                        </span>
                      </div>
                      <Input.TextArea
                        value={normalizedCoverTranslationGuidance}
                        maxLength={MAX_COVER_TRANSLATION_GUIDANCE_CHARS}
                        rows={3}
                        placeholder="Optional request, e.g. keep the original mood or make the sticker smaller"
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                          setCoverTranslationGuidance(String(e.target.value || '').slice(0, MAX_COVER_TRANSLATION_GUIDANCE_CHARS));
                        }}
                        className="cover-refresh-guidance-textarea"
                      />
                      <div className="cover-refresh-helper cover-refresh-helper--spaced">
                        Applied only when it does not conflict with text replacement, cover fidelity, or rights constraints.
                      </div>
                    </div>
                  </section>
                </>
              )}

              {mode === 'modify' && currentCoverUrl && (
                <section ref={rightsRef} className="cover-refresh-block cover-refresh-rights-block" data-testid="tutorial:cover:rights">
                  <div className="cover-refresh-section-header">
                    <div className="cover-refresh-section-title">Rights confirmation</div>
                    <Popover
                      trigger="click"
                      content={
                        <div style={{ maxWidth: 360, fontSize: 12, lineHeight: 1.4 }}>
                          <p style={{ marginBottom: 8 }}>
                            By modifying this cover, you confirm that:
                          </p>
                          <ul style={{ paddingLeft: 18, margin: 0 }}>
                            <li>You own the rights to this cover image</li>
                            <li>You have permission to create derivative works</li>
                            <li>The modified cover will comply with all applicable copyright laws</li>
                          </ul>
                        </div>
                      }
                    >
                      <a style={{ fontSize: 12 }}>View details</a>
                    </Popover>
                  </div>
                  <div className="cover-refresh-rights-control">
                    <Checkbox
                      checked={rightsConfirmed}
                      onChange={e => setRightsConfirmed(e.target.checked)}
                    >
                      <span className="cover-refresh-checkbox-copy">
                        I confirm I have the rights to modify this cover
                      </span>
                    </Checkbox>
                  </div>
                </section>
              )}

              {mode === 'generate' && (
                <section className="cover-refresh-block" data-testid="tutorial:cover:prompt">
                  <div ref={promptHeaderRef} className="cover-refresh-section-header">
                    <div className="cover-refresh-block-header cover-refresh-block-header--flush">
                      <div>
                        <h3>Prompt</h3>
                        <p>Describe the cover concept or use AI Assist to draft from metadata.</p>
                      </div>
                    </div>
                    <Space size="small">
                      <Tooltip title="AI Assist builds a market-aware prompt from your metadata">
                        <Button
                          size="small"
                          icon={<BulbOutlined />}
                          onClick={() => {
                            aiAssistTriggeredRef.current = true;
                            lastAssistKeyRef.current = aiAssistDependencyKey;
                            setPromptWaiting(true);
                            void handleAiAssist(false, aiAssistDependencyKey);
                          }}
                          loading={aiAssistLoading}
                        >
                          AI Assist
                        </Button>
                      </Tooltip>
                    </Space>
                  </div>
                  {promptWaiting ? (
                    <div className="cover-refresh-prompt-placeholder" />
                  ) : (
                    <div ref={promptRef}>
                      <Input.TextArea
                        value={customPrompt}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                          userTouchedRef.current = true;
                          setCustomPrompt(e.target.value);
                        }}
                        placeholder=""
                        autoSize={{ minRows: 8, maxRows: 10 }}
                        className="cover-refresh-prompt-textarea"
                      />
                    </div>
                  )}
                  <div ref={tipRef} className="cover-refresh-inline-note">
                    <BulbOutlined className="cover-refresh-inline-note__warning-icon" />
                    <span>
                      Tip: Add Genre, Tropes, and Keywords to your book’s metadata to help the AI design on-market covers.
                    </span>
                  </div>
                  <div className="cover-refresh-choice-row cover-refresh-choice-row--compact">
                    <Checkbox
                      checked={includeAuthor}
                      onChange={e => setIncludeAuthor(e.target.checked)}
                      disabled={!canonicalAuthorName}
                    >
                      {canonicalAuthorName
                        ? `Include author name: ${canonicalAuthorName}`
                        : 'Include author name (set an author in book metadata first)'}
                    </Checkbox>
                  </div>
                </section>
              )}
            </div>

            <div className="cover-refresh-side">
              <div className="cover-refresh-block cover-refresh-context" aria-label="Cover generation context" data-testid="tutorial:cover:run-context">
                <div className="cover-refresh-block-header">
                  <div>
                    <h3>Run Context</h3>
                    <p>Review source, output, and blockers before generation.</p>
                  </div>
                </div>
                {mode === 'modify' && currentCoverUrl && (
                  <div className="cover-refresh-current-cover">
                    <Popover
                      trigger={['hover', 'click']}
                      placement="left"
                      overlayClassName="cover-refresh-reference-popover"
                      content={(
                        <div className="cover-refresh-reference-preview">
                          <img src={currentCoverUrl} alt="Current cover reference enlarged" />
                        </div>
                      )}
                    >
                      <button
                        type="button"
                        className="cover-refresh-current-cover-button"
                        aria-label="View current cover reference larger"
                      >
                        <img src={currentCoverUrl} alt="Current cover used as the reference" />
                      </button>
                    </Popover>
                    <span>Current cover reference</span>
                  </div>
                )}
                <dl className="cover-refresh-context-list">
                  {visibleRunContext.map((item) => (
                    <React.Fragment key={item.label}>
                      <dt>{item.label}</dt>
                      <dd>{item.value}</dd>
                    </React.Fragment>
                  ))}
                  {mode === 'modify' && (
                    <>
                      <dt>Rights</dt>
                      <dd>{rightsConfirmed ? 'Confirmed' : 'Required before generation'}</dd>
                      <dt>Replacement</dt>
                      <dd>
                        {[
                          replaceTitle ? `${originalTitleText || 'Original title'} -> ${translatedTitleText || 'translated title'}` : '',
                          replaceSeries ? `${originalSeriesText || 'Original series'} -> ${translatedSeriesText || 'translated series'}` : '',
                          addLanguageSticker ? `[${displayLanguage} Edition] sticker` : '',
                        ].filter(Boolean).join('; ') || 'Select at least one cover text change'}
                      </dd>
                    </>
                  )}
                  {mode === 'generate' && (
                    <>
                      <dt>Prompt</dt>
                      <dd>{customPrompt.trim() ? 'Ready for editing and generation' : 'Waiting for prompt'}</dd>
                    </>
                  )}
                </dl>
                {mode === 'modify' && (
                  <div className="cover-refresh-context-note">
                    Text replacement preserves the artwork where possible, but the selected output will replace the localized cover after review.
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="cover-refresh-attempt-note" data-testid="tutorial:cover:attempt-note">
            <BulbOutlined className="cover-refresh-attempt-note__icon" />
            <span>
              Getting the right cover can take more than one generation attempt, especially with complex fonts or long titles.
              {' '}Image generation is priced with multiple attempts in mind, so you can create several options and choose the best fit.
            </span>
          </div>
        </div>
      </div>

      {/* Guided Tour */}
      <AppTour
        open={tourOpen}
        onClose={handleCloseTour}
        steps={(steps || []).map((s: any) =>
          s
            ? {
                ...s,
                description: renderAppTourPanel(s.description, { ravenSize: 'lg' }),
              }
            : s
        )}
      />
      </div>
    </Modal>
    <Modal
      title="Cost breakdown"
      visible={isCostBreakdownModalVisible}
      onCancel={() => setIsCostBreakdownModalVisible(false)}
      width={560}
      footer={[
	        <Button key="close-cover-cost-breakdown" onClick={() => setIsCostBreakdownModalVisible(false)} data-testid="tutorial:cover:cost-breakdown-close">
          Close
        </Button>,
      ]}
    >
      <div data-testid="cover-cost-breakdown-modal">
        <div data-testid="tutorial:cover:cost-breakdown">
          <ModalCostBreakdown
            rows={coverCostRows}
            totalCredits={coverCreditCost}
            availableCredits={numericCreditBalance}
            emptyMessage="Select cover options to calculate cost."
            formatCredits={formatCredits}
            footerNote="Credits are charged per successfully generated image."
          />
        </div>
      </div>
    </Modal>
    </>
  );
};

export default CoverGenerationModal;
